import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Space, Button, Table, Typography, Flex, Modal, Input, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, CloseOutlined, SwapOutlined } from "@ant-design/icons";
import { setFormType } from "../../../../redux/slices/roleUserSlice";
import { deleteGroupByTenant, getGroupByTenant, getUsers, searchGroups } from "../../../../services/adminServices";
import { showLoader } from "../../../../utils/showloader";
import AddEditGroup from "./AddEditGroup";
import GroupDetailScreen from "./GroupDetailScreen";
import Paginator from "../../../space/components/table/Paginator";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const mapGroups = (groups = []) => groups.map((item) => ({
    ...item,
    email: Array.isArray(item.users) ? item.users.map(val => val.email).join(', ') : ""
}))

const UserGroupManagement = (props) => {
    const { submitGroup, setSubmitGroup } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false)
    const [groups, setGroups] = useState([])
    const [users, setUsers] = useState([])
    const { formType } = useSelector(state => state.roleUser);
    const { userInfo } = useSelector(state => state.user)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [showMembers, setShowMembers] = useState(false)
    const [selectedMembers, setSelectedMembers] = useState([])
    const [showSearch, setShowSearch] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [showGroupDetail, setShowGroupDetail] = useState(false)
    const [groupDetailRecord, setGroupDetailRecord] = useState(null)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [totalCount, setTotalCount] = useState(0)


    useEffect(() => {
        if (formType === "group") {
            setShowForm(true)
        } else {
            setShowForm(false)
        }
    }, [formType])


    const fetchUsers = async () => {
        showLoader(true)
        let data = await getUsers();
        showLoader(false)

        setUsers(data.users);
    }

    useEffect(() => {
        fetchUsers();
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            const next = searchText.trim()
            const nextApplied = next.length >= MIN_SEARCH_LENGTH ? next : ""
            setAppliedSearch((prev) => {
                if (prev !== nextApplied) {
                    setPage(1)
                }
                return nextApplied
            })
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchText])

    const fetchGroups = async (currentPage = page, size = pageSize, query = appliedSearch, signal) => {
        showLoader(true)
        try {
            if (query) {
                const results = await searchGroups(query, signal)
                if (signal?.aborted) return
                const mappedGroups = mapGroups(results)
                setGroups(mappedGroups)
                setTotalCount(mappedGroups.length)
            } else {
                const data = await getGroupByTenant(currentPage, size)
                if (signal?.aborted) return
                setGroups(mapGroups(data.groups))
                setTotalCount(data.totalCount)
            }
        } finally {
            if (!signal?.aborted) {
                showLoader(false)
            }
        }
    }

    useEffect(() => {
        const controller = new AbortController()
        fetchGroups(page, pageSize, appliedSearch, controller.signal)
        return () => controller.abort()
    }, [page, pageSize, appliedSearch])

    useEffect(() => {
        if (!props.isEdit) {
            setSelectedGroup(null)
        }
    }, [props.isEdit])


    const handleEditRole = (record) => {
        dispatch(setFormType('group'))
        setSelectedGroup({ ...record })
        props.setIsEdit(true)
    }

    const handleDeleteGroup = async (record) => {
        showLoader(true)
        await deleteGroupByTenant(record.groupId)
        fetchGroups()
        showLoader(false)
    }

    const confirmGroupDelete = (record) => {
        Modal.confirm({
            title: t("Confirm"),
            content: t("Deleting the group will affect the spaces and workflows using this group. Please check the 'Group to spaces mapping' section before proceeding. Are you sure to proceed?"),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: () => {
                handleDeleteGroup(record)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const handleMemberClick = (members) => {
        setSelectedMembers(members)
        setTimeout(() => {
            setShowMembers(true)
        }, [200])
    }

    const handleGroupMap = (record) => {
        setGroupDetailRecord(record)
        setShowGroupDetail(true)
    }


    const columns = [
        {
            title: t("Group"),
            dataIndex: "name",
            key: "name",
            width: "30%"
        },
        {
            title: t("Members"),
            dataIndex: "users",
            key: "users",
            render: (text, record, rowIndex) => {
                const members = Array.isArray(record.users) ? record.users : []
                return (
                    <span className="cursor-pointer" style={{ color: "#0958d9" }} onClick={() => handleMemberClick(members)}>{members.length}{t(" Members")}</span>
                )
            }
        },
        {
            title: t('Action'),
            dataIndex: "",
            width: "10%",
            render: (text, record, rowIndex) => {
                return (
                    <Space>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditRole(record, rowIndex)}
                        />
                        <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => confirmGroupDelete(record)}
                        />
                        <Tooltip title={t("Group to spaces mapping")}>
                            <Button size="small" icon={<SwapOutlined />} onClick={() => handleGroupMap(record)} />
                        </Tooltip>
                    </Space>
                )
            },
        }
    ]


    const reloadTable = () => {
        fetchGroups();
    }

    const handleAddGroup = () => {
        dispatch(setFormType("group"))
        setShowForm(true)
        if (selectedGroup) {
            setSelectedGroup(null)
        }
    }

    const handleSearchToggle = () => {
        setShowSearch(!showSearch)
        if (showSearch) {
            setSearchText("")
            setAppliedSearch("")
        }
    }


    const locale = {
        emptyText: appliedSearch ? (
            <span>
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginBottom: "16px" }}>{t("No groups found")}</p>
            </span>
        ) : (
            <span>
                {/* <img src="/assets/RoleTable.svg" /> */}
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginBottom: "16px" }}>{t("Add new user group to manage the user data")}</p>
                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={() => {
                    dispatch(setFormType("group"))
                    setShowForm(true)
                }}>{t("New")}</Button>
            </span>
        ),
    };
    return (
        <div className="tab-content">
            {
                showGroupDetail ?
                    <GroupDetailScreen
                        selectedGroup={groupDetailRecord}
                        onBack={() => {
                            setShowGroupDetail(false)
                            setGroupDetailRecord(null)
                        }}
                    />
                : !showForm ?
                    <>
                        <Flex align="center" justify="space-between">
                            <Typography.Title level={4} style={{ margin: 0, padding: 0 }}>{t("User Group Management")}</Typography.Title>
                            <Flex gap={8} align="center" justify="flex-end">
                                {showSearch && (
                                    <Input
                                        placeholder={t("Search groups by name")}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ width: 250 }}
                                        allowClear
                                        size="large"
                                    />
                                )}
                                <Button
                                    size="large"
                                    icon={showSearch ? <CloseOutlined /> : <SearchOutlined />}
                                    onClick={handleSearchToggle}
                                    type={"default"}
                                />
                                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleAddGroup}>{t('Add Group')}</Button>
                            </Flex>
                        </Flex>
                        <div className="table-content">
                            <Table
                                className="white-header-table"
                                locale={locale}
                                columns={columns}
                                dataSource={groups}
                                pagination={false}
                                rowKey={(record) => record.groupId || record.id || record.name}
                            />
                            {appliedSearch ? (
                                groups.length > 0 && (
                                    <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
                                        <div className="text-[#000000a9]">
                                            {t("Showing")} {groups.length} {t("matching groups")}
                                        </div>
                                    </div>
                                )
                            ) : totalCount > 0 && (
                                <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
                                    <div className="text-[#000000a9]">
                                        {t("Showing")} {(page - 1) * pageSize + 1} {t("to")} {Math.min(page * pageSize, totalCount)} {t("of")} {totalCount} {t("groups")}
                                    </div>
                                    <Paginator
                                        total={totalCount}
                                        currentPage={page}
                                        pageSize={pageSize}
                                        onChange={(newPage, newPageSize) => {
                                            setPage(newPage)
                                            setPageSize(newPageSize)
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                    </>
                    : <AddEditGroup selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} submitGroup={submitGroup} setShowForm={setShowForm} reloadTable={reloadTable} users={users} userInfo={userInfo} setSubmitGroup={setSubmitGroup} />

            }
            <Modal title={t("Assigned Members")} className="columns-selector-modal" footer={false} open={showMembers} onOk={() => setShowMembers(false)} onCancel={() => setShowMembers(false)}>
                <div style={{ maxWidth: "430px", overflow: "hidden" }}></div>
                <Space direction="vertical" className="columns-selector-container" style={{ maxHeight: "400px", overflow: "overlay", paddingBottom: "16px" }}>
                    {
                        selectedMembers.map((data, index) => {
                            return <div className="checkbox-row" key={"selected-entitlement-" + index} style={{ justifyContent: "flex-start" }}>
                                <span className="step-circle">{index + 1}</span>
                                <div className="" style={{ marginLeft: "10px" }}>{data.email}</div>
                            </div>
                        })
                    }
                </Space>
            </Modal>
        </div>
    )
}

export default UserGroupManagement
